import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  Divider,
  IconButton,
  Typography,
  alpha,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ShareIcon from "@mui/icons-material/Share";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import toast from "react-hot-toast";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { writeText } from "clipboard-polyfill";
import { useCurrencyCode } from "../genericComponents/helper-functions/CurrencySymbol";

export const ReferEarnSlide = () => {
  const textToCopy = "AHFSTF";
  const currencyCode = useCurrencyCode();

  const handleCopy = () => {
    writeText(textToCopy)
      .then(() => {
        console.log("Text copied to clipboard");
        toast.success("Text copied to clipboard", {
          duration: 5000,
          style: {
            padding: "16px",
          },
        });
      })
      .catch((error) => {
        console.error("Error copying text to clipboard: ", error);
        toast.error("Error copying text to clipboard: ", {
          duration: 5000,
          style: {
            padding: "16px",
          },
        });
      });
  };

  const stepCircleSx = {
    border: `2px solid ${alpha("#1f7a4d", 0.45)}`,
    borderRadius: "999px",
    minWidth: "2rem",
    width: "2rem",
    height: "2rem",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginRight: "1rem",
    fontWeight: 700,
    flexShrink: 0,
    bgcolor: alpha("#1f7a4d", 0.06),
    color: "#1f7a4d",
  } as const;

  return (
    <Box
      sx={{
        py: { xs: 2, sm: 3 },
        pb: { xs: 12, sm: 5 },
      }}
    >
      <Box
        sx={{
          width: "100%",
          display: { xs: "block", sm: "block", md: "flex", lg: "flex" },
          gap: { md: 2 },
          alignItems: "stretch",
        }}
      >
        <Card
          elevation={0}
          sx={{
            width: { xs: "100%", md: "50%" },
            margin: {
              xs: "0 auto 1.5rem",
              sm: "0 auto 1.5rem",
              md: "0",
            },
            p: { xs: 2, sm: 2.5 },
            color: "white",
            background:
              "linear-gradient(165deg, #123524 0%, #1f7a4d 55%, #1a6b42 100%)",
            borderRadius: "20px",
            border: `1px solid ${alpha("#fff", 0.12)}`,
            boxShadow: "0 16px 40px rgba(15, 23, 17, 0.12)",
          }}
        >
          <Typography
            sx={{
              mb: 1.5,
              fontWeight: 800,
              letterSpacing: "-0.02em",
              fontSize: "1.1rem",
            }}
          >
            Refer now &amp; earn forever
          </Typography>
          <Box sx={{ display: "flex" }}>
            <CheckIcon
              sx={{ color: "white", fontSize: "18px", marginRight: "5px" }}
            />
            <Typography variant="subtitle2" sx={{ lineHeight: 1.5 }}>
              Earn {currencyCode}100 SalonTym Coins on each registration by using
              your unique referral code.
            </Typography>
          </Box>
          <Box sx={{ display: "flex" }}>
            <CheckIcon
              sx={{ color: "white", fontSize: "18px", marginRight: "5px" }}
            />
            <Typography variant="subtitle2" sx={{ lineHeight: 1.5 }}>
              SalonTym Coins for each unique referral once they complete their
              first service.
            </Typography>
          </Box>
          <Box sx={{ display: "flex" }}>
            <CheckIcon
              sx={{ color: "white", fontSize: "18px", marginRight: "5px" }}
            />
            <Typography variant="subtitle2" sx={{ lineHeight: 1.5 }}>
              Network and earn cashback on every service completion — with
              instant {currencyCode}100 off on your first booking!
            </Typography>
          </Box>
          <Box sx={{ display: "flex" }}>
            <CheckIcon
              sx={{ color: "white", fontSize: "18px", marginRight: "5px" }}
            />
            <Typography variant="subtitle2" sx={{ lineHeight: 1.5 }}>
              Every time anyone takes a service from your network, you&#39;ll
              receive a cashback of 5–20% SalonTym Coins of the total service
              amount as bonus on completion!
            </Typography>
          </Box>
          <Divider sx={{ borderColor: alpha("#fff", 0.25), my: 2 }} />
          <Typography sx={{ mb: 1.5, fontWeight: 700,
            color: alpha("#fff", 0.95),
          }}>
            Your referral code
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                borderRadius: "14px",
                overflow: "hidden",
                boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
              }}
            >
              <Typography
                sx={{
                  background: "white",
                  color: "#0b1710",
                  height: "3rem",
                  minWidth: "5.5rem",
                  px: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 800,
                  letterSpacing: "0.08em",
                }}
              >
                {textToCopy}
              </Typography>
              <Box
                aria-label="Copy referral code"
                role="button"
                tabIndex={0}
                sx={{
                  display: "grid",
                  justifyContent: "center",
                  justifyItems: "center",
                  alignItems: "center",
                  background: "#e8a400",
                  color: "#0b1710",
                  width: "4.25rem",
                  height: "3rem",
                  textAlign: "center",
                  cursor: "pointer",
                  "&:hover": {
                    background: "#d99800",
                  },
                }}
                onClick={handleCopy}
              >
                <ContentCopyIcon fontSize="small" />
                <Typography variant="caption" sx={{ fontSize: "0.65rem", fontWeight: 700 }}>
                  Copy
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: "flex", gap: 0.5 }}>
              <IconButton
                color="inherit"
                sx={{
                  bgcolor: alpha("#fff", 0.12),
                  "&:hover": { bgcolor: alpha("#fff", 0.2) },
                }}
              >
                <WhatsAppIcon sx={{ fontSize: "1.75rem" }} />
              </IconButton>
              <IconButton
                color="inherit"
                sx={{
                  bgcolor: alpha("#fff", 0.12),
                  "&:hover": { bgcolor: alpha("#fff", 0.2) },
                }}
              >
                <ShareIcon sx={{ fontSize: "1.75rem" }} />
              </IconButton>
            </Box>
          </Box>
        </Card>
        <Box
          sx={{
            width: { xs: "100%", md: "50%" },
            margin: { xs: "0 auto", md: 0 },
            p: { xs: 2, sm: 2.5 },
            borderRadius: "20px",
            border: `1px solid ${alpha("#1f7a4d", 0.15)}`,
            borderLeft: "4px solid #e8a400",
            bgcolor: "#fff",
            boxShadow: "0 12px 32px rgba(15, 23, 17, 0.06)",
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: 800, letterSpacing: "-0.02em", color: "#0b1710" }}
          >
            We value friendship
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2, mt: 0.5, lineHeight: 1.5 }}
          >
            Follow the steps below and get rewarded.
          </Typography>
          <Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "start",
                alignItems: "center",
                marginBottom: "0.5rem",
              }}
            >
              <Typography variant="subtitle2" sx={stepCircleSx}>
                1
              </Typography>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: "bold",
                }}
              >
                You shared your referral link via msg.
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "start",
                marginBottom: "0.5rem",
                alignItems: "center",
              }}
            >
              <Typography variant="subtitle2" sx={stepCircleSx}>
                2
              </Typography>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: "bold",
                }}
              >
                Refer to your friends, family, and acquaintances to our
                services. When someone from your network takes a
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "start",
                marginBottom: "0.5rem",
                alignItems: "center",
              }}
            >
              <Typography variant="subtitle2" sx={stepCircleSx}>
                3
              </Typography>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: "bold",
                }}
              >
                service and completes it, you&#39;ll receive 5-20% cashback into
                your wallet.
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "start",
                marginBottom: "0.5rem",
                alignItems: "center",
              }}
            >
              <Typography variant="subtitle2" sx={stepCircleSx}>
                4
              </Typography>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: "bold",
                }}
              >
                The cashback will be credited to your wallet once the service is
                completed by someone from your network.
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          mt: 3,
          p: { xs: 2, sm: 2.5 },
          borderRadius: "20px",
          border: `1px solid ${alpha("#1f7a4d", 0.12)}`,
          bgcolor: alpha("#f6faf8", 0.85),
        }}
      >
        <Typography
          sx={{
            fontWeight: 800,
            mb: 1.5,
            letterSpacing: "-0.02em",
            color: "#0b1710",
            fontSize: "1.1rem",
          }}
        >
          How it works
        </Typography>
        <Typography sx={{ margin: "0.5rem 0px" }}>
          Step 1: Now, let&#39;s say you refer your friend &#39;A&#39; to our
          services. Here&#39;s how the referral process could work:
        </Typography>
        <Typography sx={{ margin: "0.5rem 0px" }}>
          Step 2: &#39;A&#39; completes a service with us and you receive 5-20%
          cashback in your wallet.
        </Typography>
        <Typography sx={{ margin: "0.5rem 0px" }}>
          Step 3: &#39;A&#39; is happy with our services and refers their friend
          &#39;B&#39; to us..
        </Typography>
        <Typography sx={{ margin: "0.5rem 0px" }}>
          Step 4: &#39;B&#39; completes a service with us and you receive 5-20%
          cashback in your wallet.
        </Typography>
        <Typography sx={{ margin: "0.5rem 0px" }}>
          Step 5: &#39;B&#39; is happy with our services and refers their friend
          &#39;C&#39; to us.
        </Typography>
        <Typography sx={{ margin: "0.5rem 0px" }}>
          Step 6: &#39;C&#39; completes a service with us and you receive 5–20%
          cashback in your wallet.
        </Typography>
      </Box>
      <Box sx={{ mt: 2 }}>
        <Accordion
          elevation={0}
          disableGutters
          sx={{
            mb: 1,
            borderRadius: "14px !important",
            border: `1px solid ${alpha("#1f7a4d", 0.12)}`,
            overflow: "hidden",
            "&:before": { display: "none" },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: "#1f7a4d" }} />}
            aria-controls="panel1-content"
            id="panel1-header"
          >
            <Typography variant="body2" sx={{ fontWeight: 700, color: "#0b1710" }}>
              Tell me more about Refer &amp; Earn
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" sx={{ color: "black" }}>
              <span style={{ margin: "0.5rem 0px" }}>
                {" "}
                Let&#39;s say you&#39;re a satisfied customer of our services
                and you refer your friend Sarah to our services. Sarah then
                contacts us and books a service, which costs 5000.
              </span>{" "}
              <span>
                <br />
                <br />
                Since Sarah is in your network, you&#39;ll receive a cashback of
                5-20% of the total cost of the service (i.e., 250-1000) once the
                service is completed.
              </span>{" "}
              <br />
              <br />
              <span>
                Sarah is happy with our services and refers her cousin Alex to
                us. Alex books a service, which costs 7000.
              </span>
              <br />
              <br />
              <span>
                {" "}
                Since Sarah is part of your network, you&#39;ll receive a
                cashback of 5-20% of the total cost of Alex&#39;s service (i.e.,
                350-1400) once the service is completed. Sarah will also receive
                a cashback of 5-10% of the service cost (i.e., 350-1400) for
                referring Alex.
              </span>
            </Typography>
          </AccordionDetails>
        </Accordion>
        <Accordion
          elevation={0}
          disableGutters
          sx={{
            borderRadius: "14px !important",
            border: `1px solid ${alpha("#1f7a4d", 0.12)}`,
            overflow: "hidden",
            "&:before": { display: "none" },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: "#1f7a4d" }} />}
            aria-controls="panel2-content"
            id="panel2-header"
          >
            <Typography variant="body2" sx={{ fontWeight: 700, color: "#0b1710" }}>
              Benefits of the Refer &amp; Earn program
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" sx={{ color: "black" }}>
              Step 1: You can earn money just by referring people you know to
              our services.
              <br />
              <br />
              Step 2: You can build a network of like-minded people who are
              interested in our services.
              <br />
              <br />
              Step 3: You can potentially earn more cashback as your network
              grows.
            </Typography>
          </AccordionDetails>
        </Accordion>
        <Typography sx={{ margin: "1rem 0px", fontWeight: 700, color: "#0b1710" }}>
          You can continue to build your network and potentially earn more
          cashback as your network grows.
        </Typography>
        <Typography sx={{ margin: "1rem 0px", fontWeight: 600, color: "text.secondary", lineHeight: 1.6 }}>
          So start referring today and make the most of our Refer &amp; Earn
          program. Remember: every time anyone takes a service from your
          network, you&#39;ll receive cashback on service completion!
        </Typography>
      </Box>
    </Box>
  );
};
